<template>
  <div
    class="card"
    style="margin: 10px 0; box-shadow: 0 4px 10px -4px rgba(0, 0, 0, 0.5)"
  >
    <div style="padding: 20px 20px 0 20px">
      <h5
        class="card-title mb-0"
        style="font-size: 18px; font-weight: 900; color: #333333"
      >
        {{ statsTitle }}
      </h5>
    </div>
    <div class="card-body" style="padding: 0 20px 20px 20px !important">
      <div v-if="loading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <div v-else>
        <!-- Estadísticas compañía (admin/director + enterprise) -->
        <div
          v-if="showCompanyStats"
          class="mb-4 p-3 rounded"
          style="background-color: #f0f4f8; border: 1px solid #dee2e6"
        >
          <h6 class="mb-3 fw-bold" style="font-size: 14px">
            Estadísticas de la compañía
          </h6>
          <div v-if="loadingCompanyLeads" class="small text-muted">
            Cargando...
          </div>
          <div
            v-else
            class="d-flex flex-wrap gap-3"
            style="font-size: 13px"
          >
            <div>
              <span class="text-muted">Leads históricos:</span>
              <strong class="ms-1">{{ totalCompanyLeads }}</strong>
            </div>
            <div>
              <span class="text-muted">Ventas instaladas totales:</span>
              <strong class="ms-1">{{ companyInstalledCount }}</strong>
            </div>
            <div>
              <span class="text-muted">Porcentaje de éxito:</span>
              <strong class="ms-1">{{ companySuccessPercentage }}%</strong>
            </div>
          </div>

          <div
            v-if="leadsByStatus.length > 0"
            class="mt-3 pt-3"
            style="border-top: 1px solid #dee2e6; font-size: 13px"
          >
            <div class="text-muted small mb-2">Leads por estado:</div>
            <div
              v-for="item in leadsByStatus"
              :key="item.status"
              class="d-flex justify-content-between align-items-center py-1"
              style="gap: 8px"
            >
              <span class="d-flex align-items-center" style="gap: 6px">
                <span
                  class="status-dot"
                  :style="{ backgroundColor: getStatusDotColor(item.status) }"
                />
                {{ item.status || "Sin estado" }}:
              </span>
              <strong>{{ item.count }}</strong>
            </div>
          </div>

          <div
            v-if="leadsByCity.length > 0"
            class="mt-3 pt-3"
            style="border-top: 1px solid #dee2e6; font-size: 13px"
          >
            <div class="text-muted small mb-2">Leads por ciudad:</div>
            <div
              v-for="item in leadsByCity"
              :key="item.city"
              class="d-flex justify-content-between align-items-center py-1"
              style="gap: 8px"
            >
              <span>{{ item.city || "Sin ciudad" }}:</span>
              <strong>{{ item.count }}</strong>
            </div>
          </div>
        </div>

        <!-- Manager View -->
        <div v-if="isManager">
          <h6
            v-if="showCompanyStats"
            class="mb-2 fw-bold"
            style="font-size: 14px"
          >
            Top vendedores
          </h6>
          <div class="table-responsive">
          <table class="table table-sm" style="font-size: 12px">
            <thead>
              <tr>
                <th class="text-center">Vendedor</th>
                <th class="text-center">Ventas</th>
                <th class="text-center">Porcentaje</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="seller in salesByUser" :key="seller.userId">
                <td>{{ seller.namesList }} {{ seller.lastnamesList }}</td>
                <td class="text-center">{{ seller.salesCount }}</td>
                <td class="text-center">
                  {{ calculatePercentage(seller.salesCount) }}%
                </td>
              </tr>
              <tr class="table-active fw-bold">
                <td>Total</td>
                <td class="text-center">{{ totalSales }}</td>
                <td class="text-center">100%</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <!-- Sales Rep View -->
        <div v-else>
          <div
            style="background-color: #f8f9fa; padding: 10px; border-radius: 5px"
          >
            <h6 class="mb-2">Resumen de Ventas</h6>
            <p class="mb-1">
              Total de ventas instaladas: <strong>{{ totalSales }}</strong>
            </p>
            <p class="mb-1">
              Tus ventas: <strong>{{ userSalesCount }} </strong>
              <span v-if="salesPercentage > 0">
                ({{ salesPercentage }}% del total)</span
              >
            </p>
          </div>

          <div class="chart-container mt-3" style="max-height: 200px">
            <pie-chart
              v-if="shouldShowChart"
              :chart-data="chartData"
              :chart-options="chartOptions"
            />
            <div
              v-else
              class="mt-3"
              style="
                background-color: #f8f9fa;
                padding: 10px;
                border-radius: 5px;
              "
            >
              No hay datos suficientes para mostrar el gráfico.
            </div>
          </div>

          <div
            v-if="userSalesCount === 0"
            class="mt-3"
            style="background-color: #f8f9fa; padding: 10px; border-radius: 5px"
          >
            No tienes ventas instaladas registradas.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, h } from "vue";
import { Pie } from "vue-chartjs";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Create a PieChart component
const PieChart = defineComponent({
  name: "PieChart",
  components: {
    Pie,
  },
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  render() {
    return h(Pie, {
      data: this.chartData,
      options: this.chartOptions,
    });
  },
});

export default {
  name: "SalesStats",
  components: {
    PieChart,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    tokenAuth: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      isManager: false,
      userRole: this.$store.state.user?.role || "",
      totalSales: 0,
      salesByUser: [],
      userSales: [],
      userSalesCount: 0,
      salesPercentage: 0,
      companyLeads: [],
      loadingCompanyLeads: false,
      chartData: {
        labels: ["Mis Ventas", "Otras Ventas"],
        datasets: [
          {
            data: [0, 0],
            backgroundColor: ["#7d0c7e", "#f9cc03"],
            borderColor: ["#ffffff", "#ffffff"],
            borderWidth: 1,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.label || "";
                const value = context.raw || 0;
                const total = context.chart.getDatasetMeta(0).total;
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
      },
    };
  },
  computed: {
    showCompanyStats() {
      const role = this.$store.state.user?.role;
      const type = this.$store.state.user?.type;
      return (
        (role === "admin" || role === "director") && type === "enterprise"
      );
    },
    totalCompanyLeads() {
      return this.companyLeads.length;
    },
    companyInstalledCount() {
      return this.companyLeads.filter(
        (s) => s.status === "INSTALADO: Contrato instalado"
      ).length;
    },
    companySuccessPercentage() {
      if (this.companyLeads.length === 0) return 0;
      return Math.round(
        (this.companyInstalledCount / this.companyLeads.length) * 100
      );
    },
    leadsByStatus() {
      const counts = {};
      this.companyLeads.forEach((lead) => {
        const s = lead.status || "Sin estado";
        counts[s] = (counts[s] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => {
          const orderA = this.getStatusOrder(a.status);
          const orderB = this.getStatusOrder(b.status);
          if (orderA !== orderB) return orderA - orderB;
          return b.count - a.count;
        });
    },
    leadsByCity() {
      const counts = {};
      this.companyLeads.forEach((lead) => {
        const city = lead.promoterCity || "Sin ciudad";
        counts[city] = (counts[city] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count);
    },
    statsTitle() {
      const role = this.userRole;
      if (role === "director") {
        return "Estadísticas de Ventas";
      } else if (role === "supervisor") {
        return "Estadísticas de Ventas por Locales";
      } else if (role === "manager") {
        return "Estadísticas de Ventas por Vendedor";
      } else {
        return "Mis Estadísticas de Ventas";
      }
    },
    shouldShowChart() {
      // Check if there's any data to display
      return (
        this.totalSales > 0 &&
        this.chartData.datasets[0].data.some((value) => value > 0)
      );
    },
  },
  mounted() {
    this.fetchSalesStats();
    if (this.showCompanyStats) {
      this.fetchCompanyLeads();
    }
  },
  methods: {
    fetchCompanyLeads() {
      this.loadingCompanyLeads = true;
      this.$https
        .post("/administration/fetchCompanyLeadsForAdmin", {
          tokenAuth: this.tokenAuth,
          userId: this.userId,
        })
        .then((res) => {
          this.companyLeads = res.data.sales || [];
        })
        .catch((err) => {
          console.error("Error fetching company leads:", err);
        })
        .finally(() => {
          this.loadingCompanyLeads = false;
        });
    },
    getStatusOrder(status) {
      if (!status) return 3;
      const s = status.toUpperCase();
      if (s.includes("INSTALADO")) return 0;
      const abiertos = [
        "EN NEGOCIACIÓN",
        "NO CONTESTA",
        "PENDIENTE INSTALACIÓN",
        "PENDIENTE DOCUMENTACIÓN",
        "PENDIENTE VALIDACIÓN BIOMÉTRICA",
        "BANDEJA DE CALIDAD",
        "CRUCE DE VENTAS",
        "SIN GESTIONAR",
        "INCOMPLETO",
      ];
      if (abiertos.some((a) => s.includes(a) || s === a)) return 1;
      return 2;
    },
    getStatusDotColor(status) {
      const order = this.getStatusOrder(status);
      if (order === 0) return "#28a745";
      if (order === 1) return "#ffc107";
      if (order === 2) return "#dc3545";
      return "#6c757d";
    },
    fetchSalesStats() {
      this.loading = true;
      this.$https
        .post("/sales/fetchSalesStatsByRole/", {
          tokenAuth: this.tokenAuth,
          userId: this.userId,
        })
        .then((response) => {
          console.log("response.data", response.data);

          if (response.data.code === 200) {
            const data = response.data.data;
            this.totalSales = data.totalSales || 0;

            // Check if salesByUser exists (manager role)
            if (data.salesByUser) {
              this.isManager = true;
              this.salesByUser = data.salesByUser || [];
            } else {
              this.isManager = false;
              this.userSales = data.userSales || [];
              this.userSalesCount =
                data.userSalesCount || this.userSales.length || 0;

              // Calculate percentage
              if (this.totalSales > 0) {
                this.salesPercentage = Math.round(
                  (this.userSalesCount / this.totalSales) * 100,
                );
              }

              // Update chart data
              const otherSales = Math.max(
                0,
                this.totalSales - this.userSalesCount,
              );

              // Ensure we have valid numbers for the chart data
              this.chartData.datasets[0].data = [
                this.userSalesCount || 0,
                otherSales || 0,
              ];

              console.log("Chart data updated:", this.chartData);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching sales stats:", error);
          if (error.response?.data?.code === 401) {
            this.$store.dispatch("clearAllData");
            this.$toast.error("Sesión expirada. Inicie sesión nuevamente", {
              position: "top-right",
              max: 10,
            });
            this.$router.push({ name: "Signin" });
          } else {
            this.$toast.error(error.response?.data?.message, {
              position: "top-right",
              max: 10,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    calculatePercentage(salesCount) {
      if (this.totalSales === 0) return 0;
      return Math.round((salesCount / this.totalSales) * 100);
    },
  },
};
</script>

<style scoped>
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
